import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/client';
import { useNotification } from '../components/Notification';
import ConfirmModal from '../components/ConfirmModal';
import { SettingsIcon, InvoicesIcon } from '../components/Icons';
import './AdminInvoiceSettings.css';

function AdminInvoiceSettings() {
  const queryClient = useQueryClient();
  const { success, error: showError } = useNotification();
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, onConfirm: null, message: '', title: '', type: 'warning' });

  const { data: settings, isLoading } = useQuery({
    queryKey: ['invoice-settings'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/invoice-settings');
      return res.data;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ invoice_type, data }) => {
      return apiClient.put(`/admin/invoice-settings/${invoice_type}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['invoice-settings']);
      success('Nustatymai atnaujinti');
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Klaida atnaujinant nustatymus');
    },
  });

  const resetYearlyMutation = useMutation({
    mutationFn: async (invoice_type) => {
      return apiClient.post(`/admin/invoice-settings/${invoice_type}/reset-yearly`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['invoice-settings']);
      success('Metinis numeravimas atstatytas');
    },
    onError: (error) => {
      showError(error.response?.data?.error || 'Klaida atstatant metinį numeravimą');
    },
  });

  const handleSave = (invoice_type, data) => {
    updateMutation.mutate({ invoice_type, data });
  };

  const handleResetYearly = (invoice_type, typeName) => {
    setConfirmModal({
      isOpen: true,
      title: 'Atstatyti metinį numeravimą',
      message: `Ar tikrai norite atstatyti metinį numeravimą ${typeName}? Kitas numeris bus 1.`,
      type: 'warning',
      confirmText: 'Atstatyti',
      cancelText: 'Atšaukti',
      onConfirm: () => {
        resetYearlyMutation.mutate(invoice_type);
        setConfirmModal({ isOpen: false, onConfirm: null, message: '', title: '', type: 'warning' });
      },
      onCancel: () => setConfirmModal({ isOpen: false, onConfirm: null, message: '', title: '', type: 'warning' })
    });
  };

  const getTypeLabel = (type) => {
    const labels = {
      'invoice': 'Sąskaitos faktūros',
      'proforma': 'Išankstinės sąskaitos',
      'credit': 'Kreditinės sąskaitos'
    };
    return labels[type] || type;
  };

  const getTypeDescription = (type) => {
    const descriptions = {
      'invoice': 'Pagrindinės sąskaitos faktūros pirkėjams už apmokėtus užsakymus',
      'proforma': 'Išankstinės sąskaitos prieš atliekant apmokėjimą',
      'credit': 'Kreditinės sąskaitos grąžinimams ir kompensacijoms'
    };
    return descriptions[type] || '';
  };

  if (isLoading) {
    return (
      <div className="admin-invoice-settings-page container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Kraunama...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-invoice-settings-page container">
      <div className="settings-header">
        <div>
          <h1>
            <InvoicesIcon />
            Sąskaitų faktūrų nustatymai
          </h1>
          <p className="header-subtitle">
            Valdykite sąskaitų faktūrų, išankstinių sąskaitų ir kreditinių sąskaitų numeravimą
          </p>
        </div>
      </div>

      <div className="settings-grid">
        {settings?.map((setting) => (
          <SettingCard
            key={setting.invoice_type}
            setting={setting}
            onSave={handleSave}
            onResetYearly={handleResetYearly}
            getTypeLabel={getTypeLabel}
            getTypeDescription={getTypeDescription}
            isLoading={updateMutation.isLoading || resetYearlyMutation.isLoading}
          />
        ))}
      </div>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText={confirmModal.confirmText}
        cancelText={confirmModal.cancelText}
        onConfirm={confirmModal.onConfirm}
        onCancel={confirmModal.onCancel}
      />
    </div>
  );
}

function SettingCard({ setting, onSave, onResetYearly, getTypeLabel, getTypeDescription, isLoading }) {
  const [formData, setFormData] = useState({
    prefix: setting.prefix || '',
    next_number: setting.next_number || 1,
    number_format: setting.number_format || '{prefix}{year}{number:6}',
    reset_yearly: setting.reset_yearly !== undefined ? Boolean(setting.reset_yearly) : true
  });

  const [isEditing, setIsEditing] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(setting.invoice_type, formData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData({
      prefix: setting.prefix || '',
      next_number: setting.next_number || 1,
      number_format: setting.number_format || '{prefix}{year}{number:6}',
      reset_yearly: setting.reset_yearly !== undefined ? Boolean(setting.reset_yearly) : true
    });
    setIsEditing(false);
  };

  const previewNumber = () => {
    const currentYear = new Date().getFullYear();
    return formData.number_format
      .replace('{prefix}', formData.prefix)
      .replace('{year}', currentYear.toString())
      .replace('{number:6}', formData.next_number.toString().padStart(6, '0'));
  };

  return (
    <div className="setting-card">
      <div className="card-header">
        <div>
          <h2>{getTypeLabel(setting.invoice_type)}</h2>
          <p className="card-description">{getTypeDescription(setting.invoice_type)}</p>
        </div>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="btn btn-secondary btn-sm"
          >
            <SettingsIcon />
            Keisti
          </button>
        )}
      </div>

      {isEditing ? (
        <form onSubmit={handleSubmit} className="settings-form">
          <div className="form-group">
            <label>Prefiksas</label>
            <input
              type="text"
              value={formData.prefix}
              onChange={(e) => setFormData({ ...formData, prefix: e.target.value.toUpperCase() })}
              className="input"
              placeholder="pvz., SF, ISF, KSF"
              maxLength="20"
            />
            <span className="help-text">Prefiksas prieš sąskaitos numerį</span>
          </div>

          <div className="form-group">
            <label>Kitas numeris</label>
            <input
              type="number"
              value={formData.next_number}
              onChange={(e) => setFormData({ ...formData, next_number: parseInt(e.target.value) || 1 })}
              className="input"
              min="1"
            />
            <span className="help-text">Kitas sąskaitos numeris, kuris bus naudojamas</span>
          </div>

          <div className="form-group">
            <label>Numerio formatas</label>
            <input
              type="text"
              value={formData.number_format}
              onChange={(e) => setFormData({ ...formData, number_format: e.target.value })}
              className="input"
              placeholder="{prefix}{year}{number:6}"
            />
            <span className="help-text">
              Galimi žymenys: {'{prefix}'}, {'{year}'}, {'{number:6}'} (6 - skaitmenų kiekis)
            </span>
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.reset_yearly}
                onChange={(e) => setFormData({ ...formData, reset_yearly: e.target.checked })}
                className="checkbox"
              />
              <span>Atstatyti numeravimą kiekvienais metais</span>
            </label>
            <span className="help-text">
              Jei įjungta, numeravimas prasidės nuo 1 naujų metų pradžioje
            </span>
          </div>

          <div className="preview-section">
            <label>Peržiūra</label>
            <div className="preview-number">{previewNumber()}</div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={handleCancel}
              className="btn btn-secondary btn-sm"
              disabled={isLoading}
            >
              Atšaukti
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={isLoading}
            >
              Išsaugoti
            </button>
          </div>
        </form>
      ) : (
        <div className="settings-display">
          <div className="setting-item">
            <span className="setting-label">Prefiksas:</span>
            <span className="setting-value">{setting.prefix || '—'}</span>
          </div>
          <div className="setting-item">
            <span className="setting-label">Kitas numeris:</span>
            <span className="setting-value">{setting.next_number}</span>
          </div>
          <div className="setting-item">
            <span className="setting-label">Formatas:</span>
            <span className="setting-value code">{setting.number_format}</span>
          </div>
          <div className="setting-item">
            <span className="setting-label">Metinis atstatymas:</span>
            <span className={`status-badge ${setting.reset_yearly ? 'active' : 'inactive'}`}>
              {setting.reset_yearly ? 'Įjungtas' : 'Išjungtas'}
            </span>
          </div>
          {setting.last_reset_year && (
            <div className="setting-item">
              <span className="setting-label">Paskutinis atstatymas:</span>
              <span className="setting-value">{setting.last_reset_year} m.</span>
            </div>
          )}
          <div className="setting-item">
            <span className="setting-label">Pavyzdys:</span>
            <div className="preview-number-display">
              {setting.number_format
                .replace('{prefix}', setting.prefix)
                .replace('{year}', new Date().getFullYear().toString())
                .replace('{number:6}', setting.next_number.toString().padStart(6, '0'))}
            </div>
          </div>
          {setting.reset_yearly && (
            <button
              onClick={() => onResetYearly(setting.invoice_type, getTypeLabel(setting.invoice_type))}
              className="btn btn-secondary btn-sm"
              style={{ marginTop: '12px' }}
            >
              Atstatyti metinį numeravimą
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminInvoiceSettings;
